import * as React from 'react';
import * as Styled from './LooksInfusionSlider.styles';
import { POKey } from './constants';
import FirstTile from './FirstTile';
import LastTile from './LastTile';
import LooksSlider from './LooksSlider';
import useVariantTestId from './useVariantTestId';
import useShouldFetch from './useShouldFetch';
import { useGaTracking, useTrackingImpressionEvent, useTrackingVariationEvent } from './useTracking';
import { LooksInfusionSliderVariant } from './types';
import LooksInfusionSkeleton from './LooksInfusionSkeleton';
import useInView from 'Client/hooks/dom/useInView';
import useBellaTranslator from 'Client/hooks/useTranslator';
import HorizontalScrollableElement from 'Client/components/ScrollableElement';
import { Maybe } from 'Common/types/utility';
import useFetchingAction from 'Client/hooks/useFetchingAction/useFetchingAction';
import { fetchProductLooks, setLooksInfusionSliderViewAction } from 'Client/redux/productLooks/action';
import { useBellaDispatch, useBellaSelector } from 'Client/redux/hooks';
import { BellaTestIds } from 'Common/qaIds';
import { setLDPKnownInfoAction } from 'Client/redux/lookDetail/actions';
import useConfig from 'Client/hooks/useConfig';
import { useLaunchDarklyTracker } from 'Client/LaunchDarklyTracker';
import { Look } from 'Client/redux/productLooks/types';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';

const MAX_LOOKS_TO_FETCH = 10;

interface LooksInfusionSliderProps {
    variant: LooksInfusionSliderVariant;
}

const LooksInfusionSlider = ({ variant }: LooksInfusionSliderProps) => {
    const dispatch = useBellaDispatch();
    const launchDarklyTracker = useLaunchDarklyTracker();
    const { p100PdpLooksSlider } = useFeatureToggle();

    const t = useBellaTranslator();
    const { countryCode, currency } = useConfig();
    const sku = useBellaSelector(state => state.productdetails.productdetail.sku);
    const productLooks = useBellaSelector(state => state.productLooks);

    const firstLook = productLooks.looks?.[0];

    useFetchingAction(
        fetchProductLooks({ sku, countryCode, currency, launchDarklyTracker, limit: MAX_LOOKS_TO_FETCH })
    );

    const containerRef = React.useRef<Maybe<HTMLDivElement>>(null);
    const isElementInView = useInView<HTMLDivElement>({
        containerRef,
        options: { threshold: 0.5 },
        onVisibleCallback: () => dispatch(setLooksInfusionSliderViewAction()),
    });

    const variantTestId = useVariantTestId(variant);
    const { trackLookInfusionClicks } = useGaTracking();

    useTrackingImpressionEvent({ isElementInView });
    useTrackingVariationEvent({ isElementInView, variant });

    const onLookClick = (look: Look) => {
        dispatch(setLDPKnownInfoAction(look));
        trackLookInfusionClicks(`PDP: ${sku}, Look CTA: ${firstLook.trackingName}`);
    };

    if (productLooks.isFetching) {
        return <LooksInfusionSkeleton />;
    }

    if (productLooks.error || productLooks.looks.length === 0) {
        return null;
    }

    if (p100PdpLooksSlider && productLooks.looks.length > 1) {
        return <LooksSlider looks={productLooks.looks} />;
    }

    const shouldShowLookTile = !!firstLook?.portraitListingImage;
    const shouldShowLastTile = !!firstLook;

    const sliderTileCount = Number(shouldShowLookTile) + Number(shouldShowLastTile) + firstLook.products.length;

    return (
        <React.Fragment>
            <Styled.LineSeparator />
            <Styled.SliderContainer data-testid={variantTestId}>
                <Styled.Headline>{t(POKey.HEADLINE_LOOKS_INFUSION)}</Styled.Headline>
                <Styled.CTALink
                    to={`/looks/${firstLook?.slug}/`}
                    onClick={() => onLookClick(firstLook)}
                    testId={BellaTestIds.pdpLooksInfusionCta}
                >
                    {t(POKey.CTA_LOOKS_INFUSION)}
                </Styled.CTALink>
                <Styled.HorizontalScrollableContainer>
                    <HorizontalScrollableElement showNavArrows ref={containerRef}>
                        <Styled.ProductSlider
                            products={firstLook.products}
                            firstTile={shouldShowLookTile ? <FirstTile look={firstLook} productSku={sku} /> : null}
                            sliderLength={sliderTileCount}
                        >
                            {shouldShowLastTile && <LastTile look={firstLook} />}
                        </Styled.ProductSlider>
                    </HorizontalScrollableElement>
                </Styled.HorizontalScrollableContainer>
            </Styled.SliderContainer>
        </React.Fragment>
    );
};

export const EmptyLooksInfusionSlider = () => {
    const dispatch = useBellaDispatch();

    const containerRef = React.useRef<Maybe<HTMLDivElement>>(null);
    const isElementInView = useInView<HTMLDivElement>({
        containerRef,
        onVisibleCallback: () => dispatch(setLooksInfusionSliderViewAction()),
    });

    useTrackingVariationEvent({ isElementInView, variant: 'none' });

    return <Styled.EmptyContainer ref={containerRef} data-testid={BellaTestIds.pdpLooksInfusionVariantNone} />;
};

interface LooksInfusionSliderPlaceholderProps {
    variant: LooksInfusionSliderVariant;
}

export const LooksInfusionSliderPlaceholder = ({ variant }: LooksInfusionSliderPlaceholderProps) => {
    const containerRef = React.useRef<Maybe<HTMLDivElement>>(null);
    const isElementInView = useInView<HTMLDivElement>({
        containerRef,
        options: { rootMargin: '300px' },
    });

    const shouldStartFetching = useShouldFetch(isElementInView);

    return (
        <Styled.ObserverPlaceholder ref={containerRef} data-testid={BellaTestIds.pdpLooksInfusionObserverPlaceholder}>
            {shouldStartFetching && <LooksInfusionSlider variant={variant} />}
            {!shouldStartFetching && <LooksInfusionSkeleton />}
        </Styled.ObserverPlaceholder>
    );
};
